import type {
  IConversionResultMessage,
  IConversionStepDefinition,
  IProcessDefinition,
  ITrackingConfig,
  ITrackingStatusInfo,
  StepStatus,
  StepStatusColor,
  TrackingType,
} from '../types';

type PipelineStatusSuffix = 'WORK' | 'DONE' | 'FAIL';

interface IStepStatusUi {
  label: string;
  color: StepStatusColor;
}

const PIPELINE_STATUS_MAP: Record<PipelineStatusSuffix, StepStatus> = {
  WORK: 'inProgress',
  DONE: 'done',
  FAIL: 'fail',
};

const MANUAL_STEPS: IConversionStepDefinition[] = [
  {
    id: 1,
    title: '문서 추출',
    descriptions: {
      notStarted: '문서 준비',
      inProgress: '문서 가져오는 중',
      done: '문서 추출 완료',
      fail: '문서 추출 실패',
    },
    process: {
      key: 'DOC_EXTRACTION',
      progressLabel: '문서 추출',
    },
    subSteps: [],
  },
  {
    id: 2,
    title: '문서 분석',
    descriptions: {
      notStarted: '문서 분석 전',
      inProgress: '문서 분석 중',
      done: '문서 분석 완료',
      fail: '문서 분석 실패',
    },
    subSteps: [
      {
        id: 1,
        title: '데이터 구조화',
        process: {
          key: 'DOC_PROCESS_STRUCTURING',
          progressLabel: '데이터 구조화',
        },
      },
      {
        id: 2,
        title: '이미지 분석',
        process: {
          key: 'DOC_PROCESS_IMAGE',
          progressLabel: '이미지 분석',
        },
      },
    ],
  },
  {
    id: 3,
    title: '문서 변환',
    descriptions: {
      notStarted: '문서 변환 전',
      inProgress: '문서 변환 중',
      done: '문서 변환 완료',
      fail: '문서 변환 실패',
    },
    subSteps: [
      {
        id: 1,
        title: '데이터 분할',
        process: {
          key: 'DOC_PROCESS_CHUNKING',
          progressLabel: '데이터 분할',
        },
      },
      {
        id: 2,
        title: 'Q&A 생성',
        process: {
          key: 'DOC_PROCESS_QNA',
          progressLabel: '합성질문 생성',
        },
      },
    ],
  },
  {
    id: 4,
    title: '벡터 저장',
    descriptions: {
      notStarted: '문서 저장 전',
      inProgress: '문서 DB 저장중',
      done: '벡터 저장 완료',
      fail: '벡터 저장 실패',
    },
    process: {
      key: 'VDB_SAVE',
      progressLabel: '문서 저장',
    },
    subSteps: [],
  },
];

const AS_STEPS: IConversionStepDefinition[] = [
  {
    id: 1,
    title: 'RDB 저장',
    descriptions: {
      notStarted: 'RDB 저장 전',
      inProgress: 'RDB 저장 중',
      done: 'RDB 저장 완료',
      fail: 'RDB 저장 실패',
    },
    process: {
      key: 'RDB_SAVE',
      progressLabel: 'RDB 저장',
    },
    subSteps: [],
  },
  {
    id: 2,
    title: '벡터 저장',
    descriptions: {
      notStarted: '벡터 저장 전',
      inProgress: '벡터 저장 중',
      done: '벡터 저장 완료',
      fail: '벡터 저장 실패',
    },
    process: {
      key: 'VDB_SAVE',
      progressLabel: '벡터 저장',
    },
    subSteps: [],
  },
];

const MANUAL_SUCCESS_MESSAGES: Record<string, IConversionResultMessage> = {
  DOC_PROCESS_STRUCTURING_WORK: {
    title: '학습범위 설정하기',
    descriptions: [
      '학습 범위 설정이 완료되어야 다음 단계로 진행됩니다. 한 번 설정한 범위는 재설정이 불가능하니 신중히 설정 부탁드립니다.',
    ],
  },
  VDB_SAVE_DONE: {
    title: '학습 검수하기',
    descriptions: [
      '학습 완료되었습니다. 아래 링크에서 내용을 확인하셔서 잘못 추출된 부분은 수정 부탁드립니다. 페이지 수정의 경우 사용량이 차감됩니다. (챕터, 합성질문은 제외)',
      '마지막으로 해당 파일을 AI Agent 서비스에 적용하려면, [매뉴얼 관리]에서 적용하기를 진행해주세요.',
    ],
  },
};

function addProcessStatuses(
  statusMap: Record<string, ITrackingStatusInfo>,
  process: IProcessDefinition,
  stepId: number,
  subStepId: number,
): void {
  Object.entries(PIPELINE_STATUS_MAP).forEach(([suffix, stepStatus]) => {
    statusMap[`${process.key}_${suffix}`] = {
      stepId,
      subStepId,
      progressLabel: process.progressLabel,
      stepStatus,
    };
  });
}

function createStatusMap(steps: IConversionStepDefinition[]): Record<string, ITrackingStatusInfo> {
  return steps.reduce<Record<string, ITrackingStatusInfo>>((statusMap, step) => {
    if (step.process) {
      addProcessStatuses(statusMap, step.process, step.id, 0);
    }

    step.subSteps.forEach((subStep) => {
      addProcessStatuses(statusMap, subStep.process, step.id, subStep.id);
    });

    return statusMap;
  }, {});
}

export const STEP_STATUS_UI: Record<StepStatus, IStepStatusUi> = {
  notStarted: {
    label: '대기',
    color: 'gray',
  },
  inProgress: {
    label: '진행중',
    color: 'blue',
  },
  actionRequired: {
    label: '',
    color: 'orange',
  },
  done: {
    label: '완료',
    color: 'green',
  },
  fail: {
    label: '실패',
    color: 'red',
  },
};

export const TRACKING_CONFIGS: Record<TrackingType, ITrackingConfig> = {
  manual: {
    steps: MANUAL_STEPS,
    statusMap: {
      ...createStatusMap(MANUAL_STEPS),
      PARSE_CROP_WAIT: {
        stepId: 0,
        subStepId: 0,
        stepStatus: 'inProgress',
        progressLabel: '단계 준비 중',
      },
    },
    successMessages: MANUAL_SUCCESS_MESSAGES,
  },
  asCs: {
    steps: AS_STEPS,
    statusMap: createStatusMap(AS_STEPS),
    successMessages: {},
  },
};
